import { produce } from 'immer';
import { ApolloCache, DocumentNode } from '@apollo/client';
import {
  PhoneticLibraryPersonalQueryDocument,
  PhoneticLibraryPersonalQuery,
  PhoneticLibraryTeamQueryDocument,
  PhoneticLibraryTeamQuery,
} from '@replacements/graphql/operation.generated';
import logger from '@replacements/utils/phoneticLogger';
import {
  PhoneticLibraryType,
  PhoneticReplacement,
} from '../../../../server/graphql/__generated.types';
import {
  getFragmentName,
  useApolloCacheUtilities,
} from '../../../util/apolloUtils';

export const deriveDocumentBasedOnType = (libraryType: PhoneticLibraryType) => {
  const libraryMap: Record<
    PhoneticLibraryType,
    | typeof PhoneticLibraryPersonalQueryDocument
    | typeof PhoneticLibraryTeamQueryDocument
  > = {
    [PhoneticLibraryType.Personal]: PhoneticLibraryPersonalQueryDocument,
    [PhoneticLibraryType.Team]: PhoneticLibraryTeamQueryDocument,
  };
  return libraryMap[libraryType];
};

export const findCachedQueryByType = (
  cache: ApolloCache<unknown>,
  document: DocumentNode,
  libraryType: PhoneticLibraryType
) => {
  logger.prefix = `[findCachedQueryByType]`;
  const cachedQuery:
    | PhoneticLibraryPersonalQuery
    | PhoneticLibraryTeamQuery
    | null = cache.readQuery({
    query: document,
  });
  logger.info(
    `cachedQuery for type: ${libraryType} was ${
      cachedQuery ? '' : 'not'
    } found.`
  );
  if (!cachedQuery) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logger.info(`query document:`, (document.definitions[0] as any).name.value);
  }
  return cachedQuery;
};

export const useAddReplacementsToCache = () => {
  const cache = useApolloCacheUtilities();
  const handler = (
    activeType: PhoneticLibraryType,
    replacements?: PhoneticReplacement[]
  ) => {
    const document =
      activeType === 'team'
        ? PhoneticLibraryTeamQueryDocument
        : PhoneticLibraryPersonalQueryDocument;
    const query = cache.client.readQuery({
      query: document,
    });
    if (!query) {
      const message = `Failed to get query ${getFragmentName(document)}`;
      // eslint-disable-next-line
      console.warn(`[useAddReplacementsToCache] ${message}`);
      // Silently fail
      return;
    }
    const existingReplacements = getReplacementsFromCachedQuery(
      activeType,
      document,
      query
    );
    // should never occur - but filter out any replacements that are falsey
    const onlyValidReplacements = replacements?.filter(Boolean) || [];
    const newReplacements = [...existingReplacements, ...onlyValidReplacements];
    const newQueryResult = updateCachedQueryByType(
      activeType,
      query,
      newReplacements
    );
    cache.client.writeQuery({
      query: document,
      data: newQueryResult,
    });
  };
  return [handler];
};

export const updateCachedQueryByType = (
  type: PhoneticLibraryType,
  cachedQuery: unknown,
  newReplacements: PhoneticReplacement[]
) => {
  let updatedQuery;
  if (type === 'personal') {
    updatedQuery = produce(
      cachedQuery as PhoneticLibraryPersonalQuery,
      draft => {
        if (draft.user.phoneticLibrary.replacements_V2) {
          // eslint-disable-next-line no-param-reassign
          draft.user.phoneticLibrary.replacements_V2.personal =
            replacementsFormatter(newReplacements);
        }
      }
    );
  }
  if (type === 'team') {
    updatedQuery = produce(cachedQuery as PhoneticLibraryTeamQuery, draft => {
      if (draft.user.phoneticLibrary.replacements_V2) {
        // eslint-disable-next-line no-param-reassign
        draft.user.phoneticLibrary.replacements_V2.team =
          replacementsFormatter(newReplacements);
      }
    });
  }
  return updatedQuery;
};

export const replacementsFormatter = (replacements: PhoneticReplacement[]) => {
  return replacements.sort((a, b) =>
    a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })
  );
};

export const getReplacementsFromCachedQuery = (
  activeType: PhoneticLibraryType,
  documentToQuery: ReturnType<typeof deriveDocumentBasedOnType>,
  cachedQuery: any
): PhoneticReplacement[] => {
  let replacements: PhoneticReplacement[] = [];
  logger.prefix = `[getReplacementsFromCachedQuery]`;
  logger.info(`getting replacements for: ${activeType}`);
  logger.info(getFragmentName(documentToQuery));
  if (!cachedQuery.user) {
    logger.error(`unable to get replacements because there is no user context`);
    logger.prefix = ``;
    return [];
  }
  if (activeType === 'personal') {
    replacements =
      ((cachedQuery as PhoneticLibraryPersonalQuery).user.phoneticLibrary
        .replacements_V2?.personal as PhoneticReplacement[]) || [];
  }
  if (activeType === 'team') {
    replacements =
      ((cachedQuery as PhoneticLibraryTeamQuery).user.phoneticLibrary
        .replacements_V2?.team as PhoneticReplacement[]) || [];
  }
  if (!replacements.length) {
    logger.info(`no replacements found`);
  }
  logger.prefix = ``;
  return replacements;
};
